import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CircleCheck, CircleX, FileClock, Repeat } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/i18n';
import { useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Icon } from '@/components/ui/icon';
import { LoadingState } from '@/components/ui/states';
import { SummaryRow } from '@/components/ui/detail';
import {
  FISCAL_ROUTES,
  FISCAL_PERMISSIONS,
  FiscalDocumentStatus,
  apiMessage,
  newOperationId,
  type FiscalDocumentDetail,
  type FiscalRegimeDescription,
} from '../fiscalApi';
import { documentStatusTone, formatDateTime } from '../lib';

interface Props {
  documentId: string | null;
  onClose: () => void;
  /** A resolution changed the document — the reconciliation numbers behind this dialog are stale. */
  onChanged: () => void;
}

/**
 * ONE DOCUMENT'S COMPLETE, APPEND-ONLY HISTORY (`docs/fiscal.md` "Watching
 * it") — every attempt at the authority (what was sent, what came back) and
 * every state change the document made in response. Neither log can be edited
 * or deleted while the document exists; the database refuses, not a
 * convention, which is why both render as a plain chronological list rather
 * than something this dialog lets anyone touch.
 *
 * The check reference at the top is deliberately a single read-only line — the
 * fiscal document is its own aggregate (ADR-0005 §1) and is never presented as
 * a property of the bill it reports.
 */
export default function DocumentDetailDialog({ documentId, onClose, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const { prompt } = useConfirm();
  const [busy, setBusy] = useState<string | null>(null);

  const doc = useApi<FiscalDocumentDetail>(documentId ? FISCAL_ROUTES.document(documentId) : null);
  const regimes = useApi<FiscalRegimeDescription[]>(documentId ? FISCAL_ROUTES.regimes : null);
  const supports = regimes.data?.find((r) => r.regime === doc.data?.regime)?.supports;

  // A resolution mutates the row underneath this dialog; re-read it whenever
  // one succeeds so the badge and the button set it offers stay in sync with
  // what the server just decided, without the caller having to remember to.
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (documentId) doc.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, version]);

  if (!documentId) return null;

  const money = (amount: string) => (doc.data ? formatCurrency(amount, doc.data.currency, i18n.language) : amount);

  const run = async (action: string, request: () => Promise<unknown>, successKey: string) => {
    setBusy(action);
    try {
      await request();
      toast.success(t(successKey));
      setVersion((v) => v + 1);
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('fiscal.detail.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const retry = () =>
    run(
      'retry',
      () => api.post(FISCAL_ROUTES.retry(documentId), { operationId: newOperationId('fiscal-retry') }),
      'fiscal.detail.retried',
    );

  const submitNow = () =>
    run('submit', () => api.post(FISCAL_ROUTES.submit(documentId)), 'fiscal.detail.submitted');

  const withReason = async (
    action: string,
    titleKey: string,
    bodyKey: string,
    confirmKey: string,
    endpoint: string,
    successKey: string,
  ) => {
    const reason = await prompt({
      title: t(titleKey),
      body: t(bodyKey),
      label: t('fiscal.detail.reasonLabel'),
      confirmLabel: t(confirmKey),
      tone: 'danger',
      validate: (value) => (value.trim().length < 4 ? t('fiscal.detail.reasonTooShort') : null),
    });
    if (reason == null) return;
    await run(
      action,
      () => api.post(endpoint, { reason: reason.trim(), operationId: newOperationId(action) }),
      successKey,
    );
  };

  const cancel = () =>
    withReason(
      'cancel',
      'fiscal.detail.cancelTitle',
      'fiscal.detail.cancelBody',
      'fiscal.detail.cancelConfirm',
      FISCAL_ROUTES.cancel(documentId),
      'fiscal.detail.cancelled',
    );

  const adjust = () =>
    withReason(
      'adjust',
      'fiscal.detail.adjustTitle',
      'fiscal.detail.adjustBody',
      'fiscal.detail.adjustConfirm',
      FISCAL_ROUTES.adjust(documentId),
      'fiscal.detail.adjusted',
    );

  const abandon = () =>
    withReason(
      'abandon',
      'fiscal.detail.abandonTitle',
      'fiscal.detail.abandonBody',
      'fiscal.detail.abandonConfirm',
      FISCAL_ROUTES.abandon(documentId),
      'fiscal.detail.abandoned',
    );

  const status = doc.data?.status;
  const canRetry = can(FISCAL_PERMISSIONS.retry) && status === FiscalDocumentStatus.REJECTED;
  const canSubmit = can(FISCAL_PERMISSIONS.retry) && status === FiscalDocumentStatus.QUEUED;
  const canCancel =
    can(FISCAL_PERMISSIONS.cancel) && status === FiscalDocumentStatus.ACCEPTED && supports?.cancellation;
  const canAdjust =
    can(FISCAL_PERMISSIONS.cancel) && status === FiscalDocumentStatus.ACCEPTED && supports?.adjustment;
  const canAbandon =
    can(FISCAL_PERMISSIONS.resolve) &&
    (status === FiscalDocumentStatus.DRAFT ||
      status === FiscalDocumentStatus.QUEUED ||
      status === FiscalDocumentStatus.REJECTED);

  return (
    <Dialog
      open={Boolean(documentId)}
      onClose={onClose}
      title={doc.data ? t('fiscal.detail.title', { number: doc.data.number }) : t('fiscal.detail.loading')}
      className="max-w-2xl"
    >
      {doc.loading ? (
        <LoadingState />
      ) : doc.error || !doc.data ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50/70 p-4 text-sm dark:border-red-900 dark:bg-red-950/30">
          {doc.error ?? t('fiscal.states.loadFailedHint')}
        </div>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={documentStatusTone(doc.data.status)}>{t(`fiscal.status.${doc.data.status}`)}</Badge>
            <Badge tone="neutral">{t(`fiscal.kind.${doc.data.kind}`)}</Badge>
            <Badge tone="neutral">{t(`fiscal.environment.${doc.data.environment}`)}</Badge>
            {doc.data.attempts > 0 && (
              <span className="inline-flex items-center gap-1 text-xs text-ink-muted">
                <Icon as={Repeat} size="sm" />
                {t('fiscal.reconciliation.attempts', { count: doc.data.attempts })}
              </span>
            )}
          </div>

          <div className="space-y-1.5">
            <SummaryRow label={t('fiscal.detail.total')}>{money(doc.data.totalAmount)}</SummaryRow>
            <SummaryRow label={t('fiscal.detail.tax')}>{money(doc.data.taxAmount)}</SummaryRow>
            <SummaryRow label={t('fiscal.detail.uuid')} tone="muted">
              <span className="break-all font-mono text-xs">{doc.data.uuid}</span>
            </SummaryRow>
            <SummaryRow label={t('fiscal.detail.externalId')} tone="muted">
              <span className="break-all font-mono text-xs">{doc.data.externalId ?? '—'}</span>
            </SummaryRow>
            <SummaryRow label={t('fiscal.detail.issuedAt')} tone="muted">
              {formatDateTime(doc.data.issuedAt, i18n.language)}
            </SummaryRow>
            <SummaryRow label={t('fiscal.detail.nextAttempt')} tone="muted">
              {formatDateTime(doc.data.nextAttemptAt, i18n.language)}
            </SummaryRow>
          </div>

          {/* The check reference — a link, never an embed. The fiscal document
              is its own aggregate; this is the only line here that admits a
              check exists at all. */}
          {doc.data.check && (
            <div className="rounded-lg border p-3 text-sm">
              <p className="text-xs font-medium text-ink-muted">{t('fiscal.detail.forCheck')}</p>
              <p>
                {t('fiscal.detail.checkLine', { number: doc.data.check.number, status: doc.data.check.status })}
              </p>
            </div>
          )}

          {doc.data.lastErrorMessage && (
            <div role="alert" className="rounded-lg border border-red-300 bg-red-50/70 p-3 text-sm dark:border-red-900 dark:bg-red-950/30">
              <p className="font-mono text-xs text-red-700 dark:text-red-300">{doc.data.lastErrorCode}</p>
              <p className="mt-1">{doc.data.lastErrorMessage}</p>
            </div>
          )}

          {/* ---- operator actions ---- */}
          <div className="flex flex-wrap gap-2 border-t pt-4">
            {canRetry && (
              <Button size="sm" onClick={retry} disabled={busy !== null}>
                {t('fiscal.detail.retry')}
              </Button>
            )}
            {canSubmit && (
              <Button size="sm" variant="outline" onClick={submitNow} disabled={busy !== null}>
                {t('fiscal.detail.submitNow')}
              </Button>
            )}
            {canAdjust && (
              <Button size="sm" variant="outline" onClick={adjust} disabled={busy !== null}>
                {t('fiscal.detail.adjust')}
              </Button>
            )}
            {canCancel && (
              <Button size="sm" variant="danger" onClick={cancel} disabled={busy !== null}>
                {t('fiscal.detail.cancel')}
              </Button>
            )}
            {canAbandon && (
              <Button size="sm" variant="danger" onClick={abandon} disabled={busy !== null}>
                {t('fiscal.detail.abandon')}
              </Button>
            )}
            {status === FiscalDocumentStatus.ACCEPTED && !supports?.cancellation && !supports?.adjustment && (
              <p className="text-xs text-ink-muted">{t('fiscal.detail.noResolutionForRegime')}</p>
            )}
          </div>

          {/* ---- append-only history ---- */}
          <div>
            <p className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Icon as={FileClock} size="sm" />
              {t('fiscal.detail.submissionHistory')}
            </p>
            {doc.data.submissions.length === 0 ? (
              <p className="text-xs text-ink-muted">{t('fiscal.detail.noSubmissionsYet')}</p>
            ) : (
              <ul className="space-y-2">
                {doc.data.submissions.map((s) => (
                  <li key={s.id} className="rounded-lg border p-2 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="flex items-center gap-1 font-medium">
                        {s.outcome === 'ACCEPTED' ? (
                          <Icon as={CircleCheck} size="sm" className="text-green-600 dark:text-green-400" />
                        ) : (
                          <Icon as={CircleX} size="sm" className="text-red-600 dark:text-red-400" />
                        )}
                        {t('fiscal.detail.attempt', { n: s.attempt })} · {s.outcome}
                      </span>
                      <span className="text-ink-muted">{formatDateTime(s.createdAt, i18n.language)}</span>
                    </div>
                    {s.errorMessage && <p className="mt-1 text-red-600 dark:text-red-400">{s.errorMessage}</p>}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div>
            <p className="mb-2 text-sm font-medium">{t('fiscal.detail.eventHistory')}</p>
            <ul className="space-y-2">
              {doc.data.events.map((event) => (
                <li key={event.id} className="rounded-lg border p-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span>
                      {event.fromStatus ? t(`fiscal.status.${event.fromStatus}`) : t('fiscal.detail.created')}
                      {' → '}
                      {t(`fiscal.status.${event.toStatus}`)}
                    </span>
                    <span className="text-ink-muted">{formatDateTime(event.createdAt, i18n.language)}</span>
                  </div>
                  <p className="mt-1 text-ink-muted">{event.reason}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Dialog>
  );
}
